import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import StudentTable from './StudentTable';
import AddStudentModal from './AddStudentModal';
import { studentsAPI } from '../../api/studentsAPI';
import { schoolsAPI } from '../../api';
import { useTenant } from '../../contexts/TenantContext';

export default function SchoolStudentManagementPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { schoolUniqueId } = useParams(); // Get school unique ID from URL params
  const { getTenantUrl } = useTenant();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]); // Keep all loaded students for local filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [loadingSchool, setLoadingSchool] = useState(true);
  
  const studentsPerPage = 10;

  // Load school information
  const loadSchoolInfo = useCallback(async () => {
    if (!schoolUniqueId) return;
    
    try {
      setLoadingSchool(true);
      const token = await getAccessTokenSilently();
      const schools = await schoolsAPI.getSchools(token);
      const school = schools.find(s => s.unique_id === schoolUniqueId || s.unique_id === schoolUniqueId);
      setSchoolInfo(school);
    } catch (err) {
      console.error('Error loading school info:', err);
      setError('Failed to load school information: ' + err.message);
    } finally {
      setLoadingSchool(false);
    }
  }, [schoolUniqueId, getAccessTokenSilently]);

  // Apply local filtering and pagination
  const applyLocalFilter = useCallback(() => {
    let filteredStudents = allStudents;

    // Apply search filter
    if (searchTerm) {
      filteredStudents = allStudents.filter(student => 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.function.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Calculate pagination
    const totalFiltered = filteredStudents.length;
    const totalPagesCalculated = Math.ceil(totalFiltered / studentsPerPage);
    const startIndex = (currentPage - 1) * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    const paginatedStudents = filteredStudents.slice(startIndex, endIndex);
    
    setStudents(paginatedStudents);
    setTotalPages(totalPagesCalculated);
    setTotalStudents(totalFiltered);
  }, [allStudents, searchTerm, currentPage, studentsPerPage]);

  // Load all students from API - single fetch for this specific school
  const loadAllStudents = useCallback(async () => {
    if (!schoolInfo) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessTokenSilently();
      
      // Single large fetch for local filtering
      const filters = {
        search: '', // No server-side search needed
        page: 1,
        limit: 10000, // Load all students for local filtering
        school_id: schoolInfo.id // Filter by this specific school's ID
      };

      const result = await studentsAPI.getAllStudents(filters, token);
      
      // Transform data to match frontend expectations
      const studentsArray = Array.isArray(result) ? result : (result.students || []);
      const transformedStudents = studentsArray.map(student => ({
        id: student.id,
        name: `${student.first_name} ${student.last_name}`,
        email: student.email || '',
        function: student.enrollments?.[0]?.T_ATT_CLASSROOM?.name || 'Unassigned',
        subject: student.school?.name || schoolInfo?.name || 'Unknown School',
        status: 'Present', // Default status
        enrollmentDate: student.created_at ? new Date(student.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        first_name: student.first_name,
        last_name: student.last_name,
        school_id: student.school_id
      }));

      // Store all data and apply initial filter
      setAllStudents(transformedStudents);
      setError(null);
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load students: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, schoolInfo]);

  // Load school info first, then students
  useEffect(() => {
    loadSchoolInfo();
  }, [loadSchoolInfo]);

  // Load students when school info is available
  useEffect(() => {
    if (schoolInfo) {
      loadAllStudents();
    }
  }, [loadAllStudents, schoolInfo]);

  // Handle immediate local filtering while typing (no API calls)
  useEffect(() => {
    if (allStudents.length > 0) {
      applyLocalFilter();
    }
  }, [searchTerm, currentPage, allStudents, applyLocalFilter]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleAddStudent = async (newStudentData) => {
    try {
      const token = await getAccessTokenSilently();

      // Remove organization_id from studentData since it's now handled by JWT middleware
      const { organization_id, ...cleanStudentData } = newStudentData;
      
      const studentData = {
        first_name: cleanStudentData.first_name || cleanStudentData.name?.split(' ')[0],
        last_name: cleanStudentData.last_name || cleanStudentData.name?.split(' ').slice(1).join(' ') || 'Unknown',
        email: cleanStudentData.email,
        school_id: schoolInfo?.id // Always use this specific school's ID
      };
      
      await studentsAPI.createStudent(studentData, token);
      
      await loadAllStudents(); // Reload the list
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Failed to add student: ' + error.message);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      const token = await getAccessTokenSilently();
      await studentsAPI.deleteStudent(studentId, token);
      await loadAllStudents(); // Reload the list
    } catch (error) {
      console.error('Error removing student:', error);
      alert('Failed to remove student: ' + error.message);
    }
  };

  const handleEditStudent = (student) => {
    // Set the student to be edited and open the modal
    setEditingStudent(student);
    setShowAddModal(true);
  };

  const handleUpdateStudent = async (updatedData) => {
    try {
      const token = await getAccessTokenSilently();
      
      const updateData = {
        first_name: updatedData.first_name,
        last_name: updatedData.last_name,
        email: updatedData.email,
        school_id: schoolInfo?.id // Ensure student stays in this school
      };

      await studentsAPI.updateStudent(editingStudent.id, updateData, token);
      await loadAllStudents(); // Reload the list
      setShowAddModal(false);
      setEditingStudent(null);
    } catch (error) {
      console.error('Error updating student:', error);
      alert('Failed to update student: ' + error.message);
    }
  };

  const handleTrackStudent = (student) => {
    // Navigate to the student tracking page using tenant context
    const studentId = student.STUDENT_UNIQUE_ID || student.student_unique_id || student.id;
    const trackingUrl = getTenantUrl(`/student-tracking/${studentId}`, true);
    navigate(trackingUrl, { state: { studentId: student.STUDENT_UNIQUE_ID } });
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingStudent(null);
  };

  // Calculate pagination
  const startIndex = (currentPage - 1) * studentsPerPage;
  const endIndex = Math.min(startIndex + studentsPerPage, totalStudents);

  if (loadingSchool) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading school information...</div>
        </div>
      </div>
    );
  }

  if (!schoolInfo) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-600">School not found or access denied.</div>
        </div>
      </div>
    );
  }

  if (loading && students.length === 0) {
    return (
      <div className="mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading students...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 3v18" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">{schoolInfo.name} - Student Management</h1>
            <p className="text-slate-600">Manage students for {schoolInfo.name}</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-600">{error}</div>
          <button 
            onClick={loadAllStudents}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Try again
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Students</p>
              <p className="text-3xl font-bold">{totalStudents}</p>
            </div>
            <div className="bg-blue-400 bg-opacity-30 rounded-lg p-3">
              <svg className="w-8 h-8 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Add Student Button */}
        <button
          onClick={() => {
            setShowAddModal(true);
          }}
          className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Student
        </button>
      </div>

      {/* Student Table */}
      <StudentTable
        students={students}
        onRemove={handleRemoveStudent}
        onEdit={handleEditStudent}
        onTrack={handleTrackStudent}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalStudents={totalStudents}
        startIndex={startIndex}
        endIndex={endIndex}
        loading={loading}
      />

      {/* Add/Edit Student Modal */}
      {showAddModal && (
        <AddStudentModal
          onClose={handleCloseModal}
          onAdd={editingStudent ? handleUpdateStudent : handleAddStudent}
          editingStudent={editingStudent}
          isEditing={!!editingStudent}
          schoolContext={schoolInfo} // Pass school context to modal
        />
      )}
    </div>
  );
}
